import { Alert, Button, Divider, Stack, Typography } from '@mui/material'
import React from 'react'
import DocLink from '../../components/DocLink'
import { PersonAddAlt1, PersonSearch } from '@mui/icons-material'

const Bookings = () => {
  return (
    <Stack gap={3} divider={<Divider />}>
      <Stack gap={1}>
        <Typography variant="title3">Searching Bookings</Typography>
        <Typography>
          You can search bookings in the{' '}
          <DocLink url="/pts/bookings" text="PTS Bookings" />
          page. Pick a date window with the quick preset chips (Today,
          Tomorrow, Next 7 Days, This Month, Past 7 Days) or set your own
          dates — the arrow buttons step the window forwards or backwards by
          its own length. Free-text search (reference, patient, organisation)
          runs automatically as you type. The date range always applies, even
          when searching by reference or patient name, so if a booking
          doesn't appear, widen the dates first.
        </Typography>
        <Typography>
          The stat tiles above the grid — Journeys, Awaiting Crew, Active,
          Completed, and Exceptions — are clickable and filter the list to
          that group of statuses, so "what's running right now" or "what went
          wrong today" is one click.
        </Typography>
        <Alert severity="info" variant="outlined">
          Searching bookings and opening a booking both require two-factor
          authentication — if you haven't verified recently you'll be asked
          for a code before the list or the booking loads. Bookings carry
          patient details, so this works the same way as the Patients page.
        </Alert>
        <Typography>
          If earlier journeys were never completed, an amber{' '}
          <strong>outstanding journeys</strong> banner appears above the list
          (and on the Planning, Control, and Reports pages) showing journeys
          from previous days that never reached a final status, oldest first.
          Jump straight to the day in question to resolve them — journeys
          left in limbo never make it into billing runs, so clearing this
          banner is part of keeping invoicing complete.
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title2">Creating a Booking</Typography>
        <Typography>
          There are several ways a new PTS booking can be created:
        </Typography>
        <ul style={{ marginTop: 0, lineHeight: 2 }}>
          <li>
            PTS coordinators can visit the{' '}
            <DocLink url="/pts/bookings" text="PTS Bookings" />
            page and click <strong>New Booking</strong>.
          </li>
          <li>
            Organisations (i.e. Hospitals) can create their own booking request
            via their PTS portal login. Their account <strong>must</strong> be
            approved for their organisation, and they must provide a matching
            NHS number or Patient ID, or create a new Patient Record.
            <ul>
              <li>
                See the{' '}
                <DocLink
                  url="/patient-transport/hospital-portal"
                  text="Hospital Booking Portal"
                  docsPage
                />
                guidance for portal access levels, planned work, and live
                support. A portal request covered by a{' '}
                <strong>dedicated crew</strong> books straight onto that crew
                and is approved automatically.
              </li>
            </ul>
          </li>
          <li>
            On a dedicated duty with <strong>no dispatch</strong>, the
            clocked-in crew add their own jobs from My PTS Duty — each one
            becomes a normal booking (source: Crew) flagged for co-ordinator
            review when priced per journey. See{' '}
            <DocLink
              url="/patient-transport/crew-view"
              text="Crew View"
              docsPage
            />
            .
          </li>
        </ul>
        <Alert severity="info" variant="outlined">
          Creating or editing a booking requires the{' '}
          <strong>Co-Ordinator</strong> PTS permission. Hospital portal users
          can only create requests for their own organisation through the
          portal, and crew can only add jobs to their own clocked-in,
          self-dispatch duty.
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">
          Minimum booking dataset — create now, complete later
        </Typography>
        <Typography>
          How much patient information a booking needs up front is a
          per-tenant choice, configured under{' '}
          <DocLink
            url="/site-configuration/pts-bookings"
            text="Configuration > Patient Transport > PTS Bookings"
          />
          :
        </Typography>
        <ul style={{ marginTop: 0, lineHeight: 2 }}>
          <li>
            <strong>Standard</strong> (the default) — a full patient record is
            required before booking: name, date of birth, address, and
            mobility category.
          </li>
          <li>
            <strong>Reduced</strong> — a patient record needs only a first and
            last name. Everything else can be left blank and added later,
            which suits work booked from partial information such as a
            transfer confirmation email (repatriations, private transfers,
            broker bookings) that rarely includes an NHS number or home
            address.
          </li>
        </ul>
        <Typography>
          Either way, the details genuinely needed to plan the journey are
          always required: the journey type, the time the journey type asks
          for, and pickup/dropoff addresses that can be found on the map. An
          NHS number is never mandatory.
        </Typography>
        <Typography>
          With the reduced dataset, anything still missing that matters
          clinically — a patient identifier (date of birth, NHS number, or
          hospital number), the mobility category, or a confirmed destination
          — is tracked as <strong>outstanding details</strong>:
        </Typography>
        <ul style={{ marginTop: 0, lineHeight: 2 }}>
          <li>
            The booking page shows an amber{' '}
            <strong>"Details to confirm"</strong> banner listing exactly
            what's missing, with an Edit Patient shortcut.
          </li>
          <li>
            Assigning a crew shows the same list as a warning but never blocks
            the assignment.
          </li>
          <li>
            A journey <strong>cannot be closed as Completed</strong> until the
            outstanding details are filled in — this is the hard stop, kept
            deliberately separate from what's needed just to create the
            booking.
          </li>
        </ul>
        <Alert severity="info" variant="outlined">
          The reduced dataset applies to coordinator bookings. Hospital portal
          users keep the standard requirements unless the separate{' '}
          <strong>"Also apply to hospital portal users"</strong> switch is
          turned on. The completion block follows the current setting:
          switching back to Standard lifts it, though the "Details to
          confirm" banner stays on any booking with gaps. (Jobs crews add on
          their own duties have always had this completion check, and keep it
          regardless of the setting.)
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Patient Details</Typography>
        You must select a Patient for the booking. Either use the:
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={1}
          alignItems="center"
        >
          <Button
            color="primary"
            variant="contained"
            sx={{ borderRadius: 999 }}
            startIcon={<PersonSearch />}
          >
            Patient Lookup
          </Button>
          <Typography>or</Typography>
          <Button
            color="anPurple"
            variant="contained"
            sx={{ borderRadius: 999 }}
            startIcon={<PersonAddAlt1 />}
          >
            Add a New Patient
          </Button>
        </Stack>
        <Typography marginTop={2}>
          Two factor authentication is required before patient lookup. Search
          by NHS number or Patient ID for an exact match, or by demographics —
          surname, first name, date of birth, postcode, gender — or by a
          hospital number together with its hospital.
        </Typography>
        <Typography>
          Once selected, the patient's card is shown on the booking form with
          Edit and Remove actions. If the record has no mobility category set,
          the card warns{' '}
          <strong>
            "Mobility category not set — confirm before dispatching a vehicle"
          </strong>
          . Adding a new patient or editing an existing patient follows the
          same principles on the{' '}
          <DocLink
            url="/patient-transport/patients"
            text="Patients Docs Page"
            docsPage
          />
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Organisation & Requester</Typography>
        <Typography>
          By selecting <strong>Patient is the Customer</strong>, this area will
          no longer be required and the invoice will be addressed to the
          patient.
        </Typography>
        <Typography>
          You can select an existing organisation or create a new one. This list
          of organisations will only include organisations created within the
          PTS modules, and not include organisations created for Events, etc.
        </Typography>
        <Alert severity="warning" variant="outlined">
          Unless <strong>Patient is the Customer</strong> is ticked, a
          requesting <strong>ward or department</strong> is required whenever
          the organisation has wards set up — the booking cannot be submitted
          without one. Organisations with no wards at all (for example a
          council or a repatriation broker) skip this requirement
          automatically. Wards and departments can be added inline from the
          booking form if the right one doesn't exist yet.
        </Alert>
        <Typography>
          Once an organisation has been selected, you can then record who made
          the request. The Requested By list includes organisation portal users
          who are relevant to the selected organisation and ward, internal
          users, and the organisation's saved <strong>contacts</strong>.
        </Typography>
        <Typography>
          Contacts are for requesters who don't have a portal login — a
          social worker, discharge coordinator, or commissioning team
          contact. Use the <strong>+</strong> button inside the Requested By
          field to add one; it is saved against the organisation, so it
          appears in the list ready to pick on every future booking (and can
          be managed from the organisation's profile page). Creating contacts
          needs either the Event Planning team or the PTS Co-Ordinator
          permission.
        </Typography>
        <Typography>
          Ward access portal users appear only when they are assigned to the
          selected ward or department. Site and Admin portal users appear for the
          whole organisation, so they can be chosen even when the request is not
          tied to one ward.
        </Typography>
        <Alert severity="info" variant="outlined">
          Where requests have been submitted online by a patient or organisation
          through the booking portal, this information will be prepopulated and
          cannot be edited.
          <br />
        </Alert>
        <Typography>
          Once an organisation is selected (or Patient is the Customer is
          ticked), a billing preview appears showing which tariff would price
          this booking, how the organisation's invoices are grouped, and
          whether a quote is required before planning — the same information
          the booking page's own Billing Setup section shows after creation,
          just shown up front. If the organisation has no contract configured
          yet, this preview says so and shows the tenant defaults it would
          fall back to.
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Pickup & Dropoff</Typography>
        <Typography>
          This section stays hidden until the customer is chosen — tick{' '}
          <strong>Patient is the Customer</strong> or select an organisation
          first, because the pickup and dropoff options depend on who is
          being billed.
        </Typography>
        <Typography>
          Pick a <strong>Journey Type</strong> — Outpatient, Admission,
          Discharge, Interhospital, Transfer, Repatriation, Emergency, Risk
          Assessment, or Other. The journey type pre-fills sensible
          pickup/dropoff location types (for example Outpatient defaults to
          home → hospital, Discharge to hospital → home) and decides which
          time is asked for: a <strong>Patient Ready From</strong> time (e.g.
          Discharge, Emergency), a <strong>Target Arrival / Appointment
          Time</strong> (Outpatient), or a selector letting you choose either
          (Interhospital, Transfer, Admission, Other). Interhospital-style
          journeys also ask whether this is a{' '}
          <strong>Blue Light Transfer</strong> (Yes / No / Crew Discretion).
        </Typography>
        <Typography>
          <strong>Risk Assessment</strong> is the odd one out: a single home
          visit with no transport leg — the crew visit the patient's home
          (without the patient) to complete a transport risk assessment. The
          form shows one <strong>Visit Location</strong> stop (defaulting to
          the patient's home address) and an{' '}
          <strong>Assessment Visit Time</strong> instead of pickup and
          dropoff, and the crew work it with a shortened visit workflow. See{' '}
          <DocLink
            url="/patient-transport/risk-assessments"
            text="Risk Assessments"
            docsPage
          />
          .
        </Typography>
        <Typography>
          Each of the <strong>Pickup</strong> and <strong>Drop Off</strong>{' '}
          panels then has its own <strong>Location Type</strong>:
        </Typography>
        <ul style={{ marginTop: 0, lineHeight: 2 }}>
          <li>
            <strong>Patient's Home Address</strong> — uses the address on the
            patient record. If the selected patient has no address recorded
            (possible with the reduced minimum dataset), the form flags this
            before submission — add the address to the patient record or pick
            another location type.
          </li>
          <li>
            <strong>The customer's own hospital</strong> — shown as "{'{'}
            organisation{'}'} (Customer)" when an organisation is the
            customer. This copies the organisation's address onto the stop
            and keeps the requesting ward attached as the stop's ward, so
            ward context follows the journey onto planning, dispatch cards,
            and the hospital portal.
          </li>
          <li>
            <strong>Another Hospital / Organisation</strong> — a per-stop
            hospital picker listing every PTS organisation. Choosing one uses
            that organisation's address for the stop, independent of who is
            being billed.
          </li>
          <li>
            <strong>Other Address</strong> — manual entry with an address
            lookup that also sets the journey's map point.
          </li>
        </ul>
        <Alert severity="info" variant="outlined">
          The address lookup only appears when the stored address has no map
          point yet. Addresses that can't be matched on the map stop the
          booking with an "Address Not Found" prompt — routes and mileage
          can't be planned without a map point.
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Return journeys</Typography>
        <Typography>
          Use <strong>Create return journey</strong> when the same booking
          needs a linked journey back again. The return journey mirrors the
          outbound addresses (return pickup = outbound dropoff) and needs a{' '}
          <strong>Return Pickup Time</strong> before the booking can be
          submitted. AmbuNet stores the outward and return journeys together
          so they can be viewed and managed from the same booking record.
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Recurrence</Typography>
        <Typography>
          Tick <strong>Enable Recurrence</strong> when the same journey is
          needed repeatedly. Frequency can be <strong>Daily</strong> (with
          Mon–Sun day checkboxes), <strong>Weekly</strong> (repeat every N
          weeks), or <strong>Monthly</strong> (repeat every N months), up to
          a chosen end date. A live summary shows exactly how many journeys
          the booking will create before you submit.
        </Typography>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Planning the journey during booking</Typography>
        <Typography>
          Tick <strong>Plan the journey now</strong> to assign the journey
          while creating the booking — either <strong>Link to Existing
          Duty</strong> (duties matching the booking's date, with vehicle,
          times, and crew shown) or <strong>Create New Duty</strong> using
          the full duty form inline. Otherwise the journey remains available
          for later planning.
        </Typography>
        <Alert severity="info" variant="outlined">
          Only the first (or outbound) journey is planned onto the selected
          duty — return journeys and later recurring occurrences appear as
          unassigned journeys for the planning screens to pick up.
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Eligibility screening (portal bookings)</Typography>
        <Typography>
          If your tenant has eligibility screening turned on, hospital portal
          users see a short Yes/No questionnaire before submitting a booking —
          for example, whether the patient's condition makes other transport
          unsafe. This is optional per tenant, configured under{' '}
          <DocLink
            url="/site-configuration/pts-bookings"
            text="Configuration > Patient Transport > PTS Bookings"
          />
          , and internal coordinators creating a booking do not see it.
        </Typography>
        <Alert severity="info" variant="outlined">
          The outcome (Eligible / Review Required / Not Assessed) is recorded
          on the booking for funding audit purposes only — it never blocks or
          delays the booking being created, and answering is not compulsory.
        </Alert>
      </Stack>

      <Stack gap={1}>
        <Typography variant="title3">After submission</Typography>
        <Typography>
          AmbuNet creates the booking record and one or more journeys. The
          booking page remains the place to review requester information,
          patient details, ward context, pickup/dropoff information, linked
          outward and return legs, and journey status. Unassigned journeys are
          then picked up by planning and dispatch.
        </Typography>
      </Stack>

      <Stack gap={1}>
        <Typography variant="title3">Editing a booking</Typography>
        <Typography>
          The edit (pencil) action on a booking reopens the booking form to
          change patient, customer, addresses, times, and journey details.
          Every edit is audited — a note is added to the booking and a log
          entry records exactly which fields changed. Re-ticking the return
          journey option on a booking whose return leg was cancelled
          reinstates that leg rather than creating a duplicate.
        </Typography>
        <Alert severity="warning" variant="outlined">
          A booking can't be edited once it is Cancelled or Rejected, or once
          any invoice references it — invoiced bookings are locked so the
          billed figures stay true to the record. To change an invoiced
          booking, credit the invoice in full first, then edit and
          re-invoice. See{' '}
          <DocLink
            url="/patient-transport/invoicing"
            text="Quoting, Invoicing & Billing"
            docsPage
          />
          .
        </Alert>
      </Stack>

      <Stack gap={1}>
        <Typography variant="title3">Cancelling a journey</Typography>
        <Typography>
          Each journey on the booking page has a <strong>Cancel Journey</strong>{' '}
          button (disabled once the journey is Completed, Cancelled, No Show,
          or Aborted). Cancelling requires a reason and cannot be undone.
          Journeys are cancelled individually — there is no single button to
          cancel a whole booking, so a booking with several journeys needs
          each one cancelled in turn. Like edits, cancelling is blocked once
          the journey is on an invoice that hasn't been fully credited.
        </Typography>
      </Stack>

      <Stack gap={1}>
        <Typography variant="title3">Billing Setup</Typography>
        <Typography>
          Every booking page has a <strong>Billing Setup</strong> section
          showing exactly who's being billed and how, before it matters: the
          customer (patient or organisation), which tariff will price the
          work, the invoice grouping mode, and whether a quote is required
          before planning. If the organisation has never had a contract set
          up, this section says so and shows the tenant default tariff and
          the per-patient grouping default it's falling back to, with a link
          to set up a proper contract.
        </Typography>

        <Typography variant="title3">Quotes and invoices</Typography>
        <Typography>
          The booking page also has Quotes and Invoices sections. Generate a
          quote to auto-price the booking from the applicable tariff before
          the work happens, then send it, mark it accepted, or let a hospital
          portal Site/Admin user accept it themselves. Accepting a quote moves
          the booking into the planning pool.
        </Typography>
        <Typography>
          Once journeys are complete, use <strong>Auto-price from tariff</strong>{' '}
          on the Invoices section to generate line items from what actually
          happened (recorded times, waiting time, mobility, escorts) rather
          than typing an amount by hand. If the booking has an accepted quote,
          auto-pricing also shows how the actual price compares to what was
          quoted, and flags it when the difference is large enough to be
          worth a second look. See{' '}
          <DocLink
            url="/patient-transport/invoicing"
            text="Quoting, Invoicing & Billing"
            docsPage
          />{' '}
          for tariffs, contracts, billing runs, and credit notes.
        </Typography>
      </Stack>
    </Stack>
  )
}

export default Bookings
